import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  Modal,
  Typography,
  Pagination,
  message,
  Select,
  DatePicker,
  Button,
  Tooltip,
  Tag,
} from 'antd';

import moment from 'moment';
import { AppFactsContext } from '../../common/Navigation/Navigation';
import { fetchTasks } from '../../../services/commonAPI';
import AsyncTaskTable from './AsyncTaskTable';
import CustomButton from '../../common/Button/Button';
import { ReloadOutlined } from '@ant-design/icons';

const dateOptions = [
  {
    label: 'Today',
    value: 'Today',
  },
  {
    label: 'Yesterday',
    value: 'Yesterday',
  },
  {
    label: 'Last 7 days',
    value: 'Last 7 days',
  },
  {
    label: 'Last 30 days',
    value: 'Last 30 days',
  },
  {
    label: 'This month',
    value: 'This month',
  },
  {
    label: 'Last month',
    value: 'Last month',
  },
  {
    label: 'Custom',
    value: 'Custom',
  },
];

const AsyncTasksModal = ({ isOpen, onClose }) => {
  const { Title, Text } = Typography;

  const [tasks, setTasks] = useState([]);

  const [tableId, setTableId] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [loader, setLoader] = useState(false);

  const { appFacts } = useContext(AppFactsContext);
  const [sortField, setSortField] = useState({});
  const [filters, setFilters] = useState({ dateFilter: 'Last 7 days' });
  const InitalTime = 300;
  const [seconds, setSeconds] = useState(InitalTime);
  const intervalRef = useRef(null);
  const [notify, setNotify] = useState(false);

  const resetTime = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          handleAPICall();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  useEffect(() => {
    !loader && handleGetData('page size');
  }, [pageSize]);

  useEffect(() => {
    if (tasks[currentPage]) {
      setCurrentData(tasks[currentPage]);
    } else {
      handleGetData();
    }
  }, [currentPage]);

  useEffect(() => {
    setCurrentData(tasks[currentPage]);
  }, [tasks]);

  useEffect(() => {
    const { 'dateFilter.customDates': date, dateFilter } = filters;
    if ((dateFilter === 'Custom' && date) || dateFilter !== 'Custom') {
      handleGetData();
    }
  }, [sortField, filters]);
  const handlePageSizeChange = (_current, size) => setPageSize(size);

  useEffect(() => {
    handleGetData();
  }, [isOpen]);

  useEffect(() => {
    setNotify(false);
    return () => clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    isOpen && setNotify(true);
  }, [
    appFacts?.generate_payouts,
    appFacts?.generate_invoices,
    appFacts?.generate_transactions,
  ]);

  const transformData = (data) => {
    const { rows, columns } = data;
    let map = rows.map((row) => {
      return columns.reduce((obj, col, index) => {
        obj[col] = row[index];
        obj['key'] = obj['taskId'];
        return obj;
      }, {});
    });
    map = map.map((task) => {
      return {
        ...task,
        progress:
          task.status == 'IN_PROGRESS' && handlePercentage(task.createdAt),
        error:
          task.status === 'COMPLETED_WITH_ERRORS' || task.status == 'FAILED'
            ? true
            : false,
      };
    });
    return map;
  };

  const getPayload = () => {
    const { 'dateFilter.customDates': date, dateFilter } = filters;
    let payload_map = {};
    if (dateFilter === 'Custom' && date) {
      payload_map = { startDate: date[0], endDate: date[1] };
    }

    return {
      dateFilter,
      ...payload_map,
      [sortField.field]: sortField?.order,
      page: currentPage,
      size: pageSize,
    };
  };

  const handleGetData = () => {
    if (isOpen) {
      clearInterval(intervalRef.current);
      setLoader(true);
      const payload = getPayload();
      fetchTasks(payload)
        .then((data) => {
          setTasks({
            ...tasks,
            [`${data.pagination.currentPage}`]: transformData(data),
          });
          setTotalRecords(data?.pagination?.totalRows || 0);
        })
        .catch(() => {
          message.error('Unable to fetch tasks, please try later');
        })
        .finally(() => {
          setLoader(false);
          setSeconds(InitalTime);
          resetTime();
        });
    }
  };

  const handlePercentage = (createdAt) => {
    const time = moment().utc();
    const created = moment(createdAt);
    const diffInMinutes = moment.duration(time.diff(created)).asMinutes();
    if (diffInMinutes / 5 > 90) {
      return 90;
    } else {
      return Math.round(diffInMinutes / 5);
    }
  };

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const handleAPICall = () => {
    setNotify(false);
    if (currentPage !== 1) {
      handleApplyFilters();
    } else {
      handleGetData();
    }
  };

  const modelTitle = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px 30px 10px 10px',
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          Batch Processes
        </Title>
        <Tooltip title="Auto refresh">
          <Tag
            bordered={false}
            color="blue"
            style={{ margin: '0px 10px 5px 0px' }}
          >
            {formatTime(seconds)}
          </Tag>
        </Tooltip>
        <Tooltip title="Refresh">
          <CustomButton
            size="small"
            variant="outlined"
            icon={<ReloadOutlined />}
            onClick={() => handleAPICall()}
            shape="circle"
          />
        </Tooltip>
      </div>
      {notify && (
        <div>
          <Tag color="orange">Please refresh to see the latest data!</Tag>
        </div>
      )}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div>
            <Text>Date Requested : </Text>
          </div>
          <div
            style={{
              gap: 16,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                e !== 'Custom' && handleApplyFilters();
                setFilters({ ...filters, dateFilter: e });
              }}
              options={dateOptions}
              value={filters['dateFilter'] || 'Last 7 days'}
              defaultValue={filters['dateFilter'] || 'Last 7 days'}
            />
            {filters?.dateFilter === 'Custom' && (
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                defaultValue={filters['dateFilter?.customDates']}
                onChange={(_v, dates) => {
                  handleApplyFilters();
                  setFilters({
                    ...filters,
                    'dateFilter.customDates': dates,
                  });
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function handleApplysort(rest) {
    setTableId(() => tableId + 1);
    setTasks(() => {
      return {};
    });
    setCurrentPage(() => {
      return 1;
    });
    setSortField(() => {
      return rest;
    });
  }

  function handleApplyFilters() {
    setTableId(() => tableId + 1);
    setTasks(() => {
      return {};
    });
    setCurrentPage(() => {
      return 1;
    });
  }

  const handleClose = () => {
    setFilters({ dateFilter: 'Last 7 days' });
    setSortField({});
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      closable={true}
      footer={null}
      width={'90%'}
      title={modelTitle}
      onCancel={handleClose}
    >
      <AsyncTaskTable
        key={`table-${tableId}`}
        loading={loader}
        data={currentData}
        handleSort={handleApplysort}
        sortField={sortField}
      />
      <Pagination
        style={{
          padding: '12px 0',
          display: 'flex',
          justifyContent: 'end',
        }}
        pageSize={pageSize}
        current={currentPage}
        total={totalRecords}
        showSizeChanger={true}
        onChange={handlePageChange}
        onShowSizeChange={handlePageSizeChange}
      />
    </Modal>
  );
};

export default AsyncTasksModal;
